import { SearchClient } from 'algoliasearch';
import * as React from 'react';

import { EAttribute_InputType } from '@/graphql-types/globalTypes';

import { useAlgoliaVerifiedFilterableAttributeSchemas } from './useAlgoliaVerifiedFilterableAttributeSchemas';

export type Filter = {
  attributeSchema: {
    _id: string;
    name: string;
    options: { id: string; label: string }[];
    inputType?: EAttribute_InputType;
  };
  facet?: string;
  facets?: string[];
};

export const getFilterFacetValues = (filter: Filter): string[] =>
  filter.facets?.length ? filter.facets : filter.facet ? [filter.facet] : [];

type FilterableAttributeSchema = Filter['attributeSchema'];

export const useFilterableAttributes = (params: {
  searchClient: SearchClient | null;
  indexName: string;
  searchableAttributesField?: string;
  includeSponsorTier?: boolean;
  topLevelFilterableAttributes?: {
    _id: string;
    name: string;
    options: { id: string; label: string }[];
  }[];
  isFromMemberDirectory?: boolean;
}) => {
  const { attributeSchemas: _attributeSchemas, isLoading } =
    useAlgoliaVerifiedFilterableAttributeSchemas({
      searchClient: params.searchClient,
      indexName: params.indexName,
      searchableAttributesField: params.searchableAttributesField,
      isFromMemberDirectory: params.isFromMemberDirectory,
    });

  const topLevelFilterableAttributes = params.topLevelFilterableAttributes;
  const searchableAttributesField = params.searchableAttributesField;

  const topLevelFilterableAttributeIds = React.useMemo(
    () =>
      topLevelFilterableAttributes?.map(
        (topLevelAttribute) => topLevelAttribute._id
      ) ?? [],
    [topLevelFilterableAttributes]
  );

  const attributeSchemas = React.useMemo<FilterableAttributeSchema[]>(
    () =>
      topLevelFilterableAttributes
        ? [...topLevelFilterableAttributes, ..._attributeSchemas]
        : _attributeSchemas,
    [topLevelFilterableAttributes, _attributeSchemas]
  );

  const [_selectedFilters, setSelectedFilters] = React.useState<Filter[]>([]);

  const selectFilter = React.useCallback(
    (filterParams: {
      attributeSchemaId: string;
      facet?: string;
      facets?: string[];
    }): void => {
      const attributeSchema = attributeSchemas.find(
        (schema) => schema._id === filterParams.attributeSchemaId
      );

      if (attributeSchema) {
        setSelectedFilters((previousValue) => [
          ...previousValue.filter(
            (filter) =>
              filter.attributeSchema._id !== filterParams.attributeSchemaId
          ),
          {
            attributeSchema,
            facet: filterParams.facet,
            ...(filterParams.facets ? { facets: filterParams.facets } : {}),
          },
        ]);
      }
    },
    [attributeSchemas]
  );

  const unselectFilter = React.useCallback(
    (filterParams: { attributeSchemaId: string }) => {
      setSelectedFilters((previousValue) =>
        previousValue.filter(
          (filter) =>
            filter.attributeSchema._id !== filterParams.attributeSchemaId
        )
      );
    },
    []
  );

  const facetFilters: string[] = React.useMemo(() => {
    return searchableAttributesField
      ? _selectedFilters.flatMap((filter) => {
          const { attributeSchema } = filter;
          const prefix = topLevelFilterableAttributeIds.includes(
            attributeSchema._id
          )
            ? attributeSchema._id
            : `${searchableAttributesField}.${attributeSchema._id}`;
          const values = getFilterFacetValues(filter);

          return values.length
            ? values.map((value) => `${prefix}:${value}`)
            : [prefix];
        })
      : [];
  }, [
    searchableAttributesField,
    _selectedFilters,
    topLevelFilterableAttributeIds,
  ]);

  const unselectedFilters: Filter[] = React.useMemo(() => {
    const selectedIds = _selectedFilters.map(
      (filter) => filter.attributeSchema._id
    );

    return attributeSchemas
      .filter((schema) => !selectedIds.includes(schema._id))
      .map((schema) => ({ attributeSchema: schema, facet: undefined }));
  }, [attributeSchemas, _selectedFilters]);

  const selectedFilters: Filter[] = React.useMemo(() => {
    return [..._selectedFilters].sort((a, b) => {
      if (a.attributeSchema.name < b.attributeSchema.name) {
        return -1;
      }
      if (a.attributeSchema.name > b.attributeSchema.name) {
        return 1;
      }

      return 0;
    });
  }, [_selectedFilters]);

  return {
    selectedFilters,
    unselectedFilters,
    selectFilter,
    unselectFilter,
    isLoadingFilters: isLoading,
    facetFilters,
  };
};
